import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";

import type { Transport } from "@tdelivr/api-client";
import { services } from "@tdelivr/api-client";
// The row shape MOB-01 publishes, imported rather than restated. A field here that the service
// stopped returning is a build failure, not a blank cell.
import type { RideProductVersion } from "@tdelivr/api-client/generated/mobility/ride-product-catalog.js";

import {
  capacityText,
  cataloguePage,
  fetchNote,
  rowDetail,
  stateOf,
  stateReason,
  typeText,
  type CatalogueOrder,
} from "../catalogue.ts";
import { stateForError, stateForList, type ViewState } from "../view-state.ts";
import { PageHeading } from "./PageHeading.tsx";
import { StateBadge } from "./StateBadge.tsx";
import { StateView } from "./StateView.tsx";

/**
 * Service catalogue: the ride classes a customer can book from this tenant right now.
 *
 * Built to `17 WP16 Tenant Admin.dc.html`, page `catalogue`, against MOB-01
 * `GET /v1/ride-products`.
 *
 * **R-12 rejected this page and the rejection was wrong in a useful way.** It asked of each of the
 * design's fifteen pages whether an operation answers a list of the things the page is a list of,
 * and put `catalogue` under "the service does not exist": the design cites `CAT-01` and `CAT-02`
 * and the catalogue of 142 published contracts answers to neither. Both halves of that remain
 * true. What it did not ask is whether a service the design does **not** cite answers the page's own
 * question — and MOB-01 does: public, tenant-scoped, one read scope, and two of the design's three
 * columns exactly. `4 seats · 2 bags` is `passengerCapacity` and `luggageCapacity` in the design's
 * own grammar. R-17 in `docs/design/DESIGN_RECONCILIATION.md` is the measurement.
 *
 * **`lifecycle` can only ever read `active` here, and the contract publishes it as a three-value
 * enum.** MOB-01's listing selects `lifecycle='active'` and the effective window, and the column
 * and the stored state are written from the same value, so a draft or retired class does not appear
 * at all — and nothing in the response says how many were left out. A page titled "Service
 * catalogue" answering with the subset bookable at one instant, under a state column reading
 * `active` on every row, is a complete-looking catalogue with records missing from it. An
 * administrator who has just created a draft class and does not find it here will reasonably
 * conclude the create failed. So the board says under its grid what the listing is a list of.
 *
 * **`serviceAreaIds` restricts nothing, and the design's badge says it restricts three zones.**
 * MOB-01 publishes up to 500 service-area identifiers per product and **no service on this platform
 * acts on the field** — searched across every `src/` under `services/` and `packages/`. EXP-004
 * reads it, resolving each identifier to the zone GEO-06 names, and refuses nothing on the strength
 * of the list; the other occurrences outside MOB-01's own code and tests are the generated client's
 * type and one `[]` in a ride-quote test fixture. A badge reading `3 zones only` would tell an
 * administrator a restriction is in force while the platform offers the class everywhere, and that
 * error reaches a customer. So the badge carries the measured fact.
 *
 * **Half the design's `Type` column has no source.** `ride` and `delivery` are its two values;
 * DEL-01 owns delivery products and publishes no listing — its only read needs a `productId` the
 * page exists to discover and a `geographyId` that no operation on this platform answers at all.
 */

const productsApi = services.mobility__ride_product_catalog;

/** The design's own rows-per-page choices, in its order. */
const PER_PAGE_CHOICES = [5, 10, 25, 50] as const;

/** The design's sort options, in its order and its words. */
const ORDER_CHOICES: readonly { readonly value: CatalogueOrder; readonly label: string }[] = [
  { value: "default", label: "Default order" },
  { value: "state", label: "State" },
  { value: "az", label: "Name A–Z" },
  { value: "za", label: "Name Z–A" },
];

/**
 * The states the toolbar's filter offers.
 *
 * The two the listing can answer, and no more. MOB-01's other two reads do return `draft` and
 * `retired` and this listing cannot, so offering those options would be offering a filter that
 * always empties the board — which reads as "you have none" rather than "this operation never
 * answers one". The filter is computed here because the listing's only query parameter is `at`.
 */
const STATE_CHOICES: readonly string[] = ["active", "area list not enforced"];

/** The export formats the design's menu offers, in its order and its words. */
const EXPORTS = [
  { format: "csv", glyph: "description", label: "CSV" },
  { format: "xls", glyph: "table_view", label: "Excel (.xls)" },
  { format: "pdf", glyph: "picture_as_pdf", label: "PDF" },
] as const;

type ExportFormat = (typeof EXPORTS)[number]["format"];

export interface ServiceCatalogueProps {
  readonly transport: Transport;
  /** Innermost last: tenant, group, page. Rendered in the page body, never in the top bar. */
  readonly crumbs?: readonly string[];
}

export function ServiceCatalogue({ transport, crumbs }: ServiceCatalogueProps): ReactNode {
  const [products, setProducts] = useState<readonly RideProductVersion[] | undefined>(undefined);
  const [failure, setFailure] = useState<ViewState | undefined>(undefined);
  const [search, setSearch] = useState("");
  const [state, setState] = useState<string | undefined>(undefined);
  const [order, setOrder] = useState<CatalogueOrder>("default");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState<number>(10);
  const [exportOpen, setExportOpen] = useState(false);
  const exportRegion = useRef<HTMLSpanElement>(null);

  const load = useCallback(async () => {
    const client = productsApi.createRideProductCatalogClient(transport);
    try {
      /*
       * No `at`, which is the listing's one query parameter.
       *
       * Its own description settles it: "Answer the products in effect at this moment. Now when
       * omitted." A board that sent an instant would be sending the browser's clock where the
       * service has its own, and a class whose window opens in the next second would flicker
       * between two readings of the same page. Omitted is the service's answer about now.
       */
      const answered = await client.getRideProducts();
      setProducts(answered.products);
      setFailure(undefined);
    } catch (error) {
      setFailure(stateForError(error, ["ride-product:read"]));
      setProducts(undefined);
    }
  }, [transport]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!exportOpen) return undefined;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setExportOpen(false);
    };
    const onPointer = (event: MouseEvent): void => {
      const region = exportRegion.current;
      if (region !== null && !region.contains(event.target as Node)) setExportOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onPointer);
    return (): void => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onPointer);
    };
  }, [exportOpen]);

  const view = useMemo(
    () =>
      cataloguePage(products ?? [], {
        search,
        order,
        page,
        perPage,
        ...(state === undefined ? {} : { state }),
      }),
    [products, search, order, page, perPage, state],
  );

  /*
   * No `asOf`, so no stale state — the sixth screen to need that field.
   *
   * MOB-01's listing is explicitly an answer about an instant and publishes no instant: not the
   * `at` it used, not a stamp of its own. So a board that holds the answer for a minute has no way
   * to say the minute passed, and the recommendation R-10 made — publish an `asOf` beside the rows
   * — applies here with more force than anywhere else so far, because this listing's whole meaning
   * is "as at".
   */
  const viewState = failure ?? stateForList(products, { filtered: state !== undefined });

  const clearFilters = (): void => {
    setSearch("");
    setState(undefined);
    setPage(1);
  };

  const note = fetchNote(products?.length);

  /*
   * The heading row, hoisted because the loading state draws it too.
   *
   * In `02 Console Shell v3` the headings sit outside the state switch: the design shimmers the
   * rows beneath a heading row that never moves, so the board does not jump when the data lands.
   * One element rather than two copies of this grid, which would be free to disagree. Q-287.
   */
  const head = (
    <div className="records__head">
      <span>Service</span>
      <span>Type</span>
      <span>Capacity</span>
      <span className="records__head--right">State</span>
    </div>
  );

  return (
    <section className="page records">
      <PageHeading
        crumbs={crumbs ?? []}
        title="Service catalogue"
        /*
         * `MOB-01`, where the design's chip reads `CAT-01 · CAT-02`.
         *
         * Neither of the design's codes exists in any published contract, which is the error
         * `tests/service-codes.test.ts` exists to make impossible to reintroduce. The chip names
         * the service the page actually reads, because the chip is how an administrator reports
         * what they were looking at.
         */
        source="MOB-01"
        /*
         * The design's sentence, with one clause removed and the reason put in its place.
         *
         * The design writes "Ride classes, delivery types and what each one offers." Delivery types
         * are DEL-01's and DEL-01 publishes no listing, so naming them on a page that cannot
         * contain them would make the page read as broken. Q-36 records the replacement.
         */
        description="Ride classes and what each one offers. Delivery types are not here: nothing lists them."
        action={
          <button
            type="button"
            className="records__primary"
            /*
             * Drawn, and refused.
             *
             * MOB-01's only write is `PUT /v1/ride-products/{productId}` with an `expectedVersion`
             * — so creating is not a create, and the form would have to invent an identifier, which
             * is the same wall the zones board's `Add zone` hit on GEO-06.
             *
             * Three of the design's nine fields have no field on the wire: `Max weight (kg)` is a
             * delivery constraint and a ride product has none, `Errand` is one of three choices
             * under `Type` and no service on the platform owns an errand, and `Accessibility` would
             * have to invent the vocabulary `supportedRiderCapabilities` holds as opaque strings.
             * Three the contract **requires** have no field on the form: `vehicleClass`,
             * `lifecycle` and `effectiveFrom`. And the one field the form offers that the contract
             * does accept — `Zones`, writing `serviceAreaIds` — restricts nothing, because no
             * service acts on it.
             */
            disabled
            title="MOB-01's only write is a PUT to an existing productId with an expectedVersion, so there is no create. Max weight, Errand and Accessibility have no field on a ride product; the required vehicleClass, lifecycle and effectiveFrom have no field on the form; and Zones writes a field no service acts on. See R-17."
          >
            <span aria-hidden="true" className="records__primary-glyph">
              add
            </span>
            Add service
          </button>
        }
      />

      {/*
        No tiles, where the design has four — and all four count the same absence.

        `Services · 9`, `Drafts · 2`, `Hidden in some zones · 3` and `Retired · 1`. R-04 is why the
        first is not a count of the rows held: no listing on this platform publishes a total. The
        second and the fourth count rows **this operation cannot answer at all** — its own predicate
        excludes every draft and every retired product — so they would read 0 under labels meaning
        "none exist", which is a stronger claim than "none were returned". The third counts a
        restriction nothing enforces. R-17.
      */}

      <div className="records__panel">
        <div className="records__toolbar">
          <span className="records__search">
            <span aria-hidden="true" className="records__search-glyph">
              search
            </span>
            <input
              id="catalogue-search"
              type="search"
              aria-label="Search"
              placeholder="Search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
            />
          </span>
          <select
            id="catalogue-state"
            aria-label="Filter by state"
            value={state ?? ""}
            onChange={(event) => {
              setState(event.target.value === "" ? undefined : event.target.value);
              setPage(1);
            }}
          >
            <option value="">All states</option>
            {STATE_CHOICES.map((choice) => (
              <option key={choice} value={choice}>
                {choice}
              </option>
            ))}
          </select>
          <select
            id="catalogue-order"
            aria-label="Sort"
            value={order}
            onChange={(event) => {
              setOrder(event.target.value as CatalogueOrder);
            }}
          >
            {ORDER_CHOICES.map((choice) => (
              <option key={choice.value} value={choice.value}>
                {choice.label}
              </option>
            ))}
          </select>
          <span className="records__export" ref={exportRegion}>
            <button
              type="button"
              className="records__export-button"
              aria-expanded={exportOpen}
              onClick={() => {
                setExportOpen((open) => !open);
              }}
            >
              <span aria-hidden="true" className="records__export-glyph">
                download
              </span>
              Export
            </button>
            {exportOpen ? (
              <div className="records__export-menu" role="menu">
                {EXPORTS.map((choice) => (
                  <button
                    key={choice.format}
                    type="button"
                    role="menuitem"
                    className="records__export-item"
                    onClick={() => {
                      setExportOpen(false);
                      exportRows(view.all, choice.format);
                    }}
                  >
                    <span aria-hidden="true" className="records__export-item-glyph">
                      {choice.glyph}
                    </span>
                    {choice.label}
                  </button>
                ))}
              </div>
            ) : null}
          </span>
        </div>

        <StateView
          state={viewState}
          purpose="Ride classes appear here as soon as one is active and inside its effective window."
          onRetry={() => {
            void load();
          }}
          skeleton={{
            head,
            columns: 4,
            rowClassName: "records__row",
            // Both strings come from the trail the shell already draws, so the
            // sentence can never disagree with the breadcrumb above it.
            page: crumbs?.at(-1) ?? "this page",
            tenant: crumbs?.[0] ?? "this tenant",
          }}
        >
          {head}
          <div className="records__rows">
            {view.rows.map((product) => {
              const shown = stateOf(product);
              return (
                <div
                  key={product.productId}
                  className={
                    product.serviceAreaIds.length > 0
                      ? "records__row records__row--cross"
                      : "records__row"
                  }
                  title={rowDetail(product)}
                >
                  <span className="records__when">{product.displayName}</span>
                  <span className="records__actor">{typeText()}</span>
                  <span className="records__action">{capacityText(product)}</span>
                  <span className="records__state">
                    <StateBadge
                      code="MOB-01"
                      status={product.lifecycle}
                      classKey={shown.classKey}
                      label={shown.label}
                      title={stateReason(product)}
                    />
                  </span>
                </div>
              );
            })}
          </div>
          {view.total === 0 ? (
            <div className="records__no-match">
              <span aria-hidden="true" className="records__no-match-glyph">
                search_off
              </span>
              Nothing matches this search or filter.
              <button type="button" className="records__clear" onClick={clearFilters}>
                Clear filters
              </button>
            </div>
          ) : null}
          <div className="records__pager">
            <span>
              {view.total === 0
                ? "No matching rows"
                : `Showing ${(page - 1) * perPage + 1}–${Math.min(page * perPage, view.total)} of ${view.total}`}
            </span>
            <span className="records__pager-controls">
              <select
                id="catalogue-per-page"
                aria-label="Rows per page"
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
              >
                {PER_PAGE_CHOICES.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice} per page
                  </option>
                ))}
              </select>
              <button
                type="button"
                aria-label="Previous page"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                <span aria-hidden="true">chevron_left</span>
              </button>
              <span className="records__page">
                Page {Math.min(page, view.pageCount)} of {view.pageCount}
              </span>
              <button
                type="button"
                aria-label="Next page"
                disabled={page >= view.pageCount}
                onClick={() => setPage((current) => Math.min(view.pageCount, current + 1))}
              >
                <span aria-hidden="true">chevron_right</span>
              </button>
            </span>
          </div>
        </StateView>

        {/*
          What the listing is a list of, which is not what the page is called.

          The pager pages what is held, honestly — MOB-01 answers everything matching in one
          request. What the response cannot say is how much it excluded: its predicate drops every
          draft and every retired product and publishes no count of them, no total and no cursor.
          Always present, because the fact is always true.
        */}
        {note === undefined ? null : (
          <p className="records__bounded" role="status">
            {note}
          </p>
        )}
      </div>

      {/*
        The closing callout, in the design's own `info` style — and the second place on this console
        where the design's words are not kept.

        The design writes "Retiring a service keeps its history and receipts readable." MOB-01's
        history genuinely is immutable: `ride_product_versions` is append-only and
        `GET /v1/ride-products/{productId}?version=N` reads any version ever written. **The problem
        is reaching it.** A `productId` is published by exactly one public operation — this listing —
        and this listing excludes retired products. So retiring a class removes the only published
        route to its own identifier, and from this console its history becomes unreachable at the
        moment the sentence promises it stays readable. That is the design's sentence inverted
        rather than merely unsupported, so the callout carries the measured fact and Q-37 records
        that the words are written here.
      */}
      <div className="records__note records__note--info">
        <span aria-hidden="true" className="records__note-glyph">
          info
        </span>
        <span>
          Retiring a service keeps every version of it stored, and removes it from this list — which
          is the only place its identifier is published, so keep a note of it before you retire it.
        </span>
      </div>
    </section>
  );
}

/**
 * Export what the administrator is looking at, in the columns they are looking at.
 *
 * The filtered and ordered set rather than the page, and the words on screen — so an exported row
 * says the same thing the board did, including the correction on the state.
 */
function exportRows(rows: readonly RideProductVersion[], format: ExportFormat): void {
  const header = ["Service", "Type", "Capacity", "State"];
  const body = rows.map((product) => [
    product.displayName,
    typeText(),
    capacityText(product),
    stateOf(product).label,
  ]);

  if (format === "csv") {
    const quote = (value: string): string => `"${value.replaceAll('"', '""')}"`;
    const csv = [header, ...body].map((line) => line.map(quote).join(",")).join("\n");
    download(new Blob([csv], { type: "text/csv" }), "catalogue.csv");
    return;
  }

  const table =
    '<table border="1" cellpadding="6" style="border-collapse:collapse;font-family:sans-serif;font-size:12px">' +
    `<tr>${header.map((cell) => `<th>${escapeHtml(cell)}</th>`).join("")}</tr>` +
    body
      .map((line) => `<tr>${line.map((cell) => `<td>${escapeHtml(cell)}</td>`).join("")}</tr>`)
      .join("") +
    "</table>";

  if (format === "xls") {
    download(
      new Blob([`<html><head><meta charset="utf-8"></head><body>${table}</body></html>`], {
        type: "application/vnd.ms-excel",
      }),
      "catalogue.xls",
    );
    return;
  }

  const printable = window.open("", "_blank");
  if (printable === null) return;
  printable.document.write(
    `<html><head><title>catalogue</title></head><body><h2 style="font-family:sans-serif">catalogue</h2>${table}</body></html>`,
  );
  printable.document.close();
  printable.focus();
  printable.print();
}

function escapeHtml(value: string): string {
  return value.replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;");
}

function download(blob: Blob, name: string): void {
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = name;
  document.body.append(link);
  link.click();
  setTimeout(() => {
    URL.revokeObjectURL(link.href);
    link.remove();
  }, 500);
}
